import * as React from 'react';
import Grid from '@mui/material/Grid2';
import { observer } from 'mobx-react';
import { Box, Link, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import BaseButton from '../../../../components/BaseButton';
import BaseInput from '../../../../components/BaseInput';
import BaseRadioButtons from '../../../../components/BaseRadioButtons';
import BaseDateRangePicker, {
  INVALID_DATE_RANGE,
} from '../../../../components/BaseDateRangePicker';
import { LAUNCH_BUTTON } from '../../../../constants/base';
import DataStore from '../../../../store';
import { validateForm } from '../../../../helpers/formValidator';
import { notificationStore } from '../../../../store/Notification';
import { MISSING_FIELDS_MESSAGE } from '../../../../helpers/constants';
import { getIdFromLocalStorage } from '../../../../helpers/base';
import socketStore from '../../../../store/Socket';
import ProgressBar from '../../../../components/ProgressBar';
import { validationRules } from '../../../../helpers/validationRules';
import { getPageConfiguration } from '../../../../helpers/groups';
import { useSocketListener } from '../../../../hooks/useSocketListener';
import { ProgressPayload } from '../../../../types/global';

const Metrics = observer(() => {
  const [progress, setProgress] = React.useState(0);
  const [progressMax, setProgressMax] = React.useState(0);
  const [progressMessage, setProgressMessage] = React.useState('');

  const DATE_RANGE_LAST_DAY = 'last day';
  const DATE_RANGE_LAST_WEEK = 'last week';
  const DATE_RANGE_LAST_MONTH = 'last month';
  const DATE_RANGE_NO_LIMIT = 'clear';

  const resetProgressBar = () => {
    setProgress(0);
    setProgressMax(0);
    setProgressMessage('');
  };

  React.useEffect(() => {
    showOutput();
  }, []);

  const socket = socketStore.socket;
  const channel = React.useMemo(
    () => `user_update_${getIdFromLocalStorage()}`,
    []
  );
  const onProgress = React.useCallback((data: ProgressPayload): void => {
    setProgress(data.progress ?? 0);
    setProgressMax(data.total ?? 0);
    setProgressMessage(data.message ?? '');
  }, []);
  useSocketListener<ProgressPayload>(socket, channel, onProgress, true);

  const fieldLabels: { [key: string]: string } = {
    maxFiles: 'Max Files',
    slideNumber: 'Slide Number',
    wells: 'Wells',
    keyword: 'Keyword',
    metricsType: 'Metrics Type',
    dateRange: 'Date Range',
  };

  const [errors, setErrors] = React.useState({});

  const [data, setData] = React.useState({
    keyword: '',
    metricsType: 'QC',
    maxFiles: null as number | null,
    slideNumber: '',
    wells: '',
    dateRange: null as string | null,
  });
  const isMaxFilesRequired = !data.slideNumber?.trim();
  const validationSchema = {
    slideNumber: validationRules.slideNumber,
    keyword: validationRules.keyword,
    ...(isMaxFilesRequired && {
      maxFiles: {
        type: 'number',
        min: 1,
        max: 10000,
        required: true,
        message: 'Max Files is required if Slide Number is not filled',
      },
    }),
    wells: {
      ...validationRules.wells,
      custom: (value: string) => {
        if (!data.slideNumber?.trim()) {
          return 'Slide Number must be filled before entering Wells';
        }
        return validationRules.wells.custom?.(value);
      },
    },
  };
  const onChangeData = (event: any) => {
    const { name, value }: any = event.target;

    setData((prevData: any) => {
      const newData = {
        ...prevData,
        [name]: value,
      };

      if (name === 'slideNumber' && !value.trim()) {
        newData.wells = '';
      }

      return newData;
    });
  };

  const showOutput = (resData: any = {}) => {
    DataStore.showOutput('Metrics launch', resData, 'html');
  };

  const getDateRangeValues = (type: string, number: number) => {
    const now = DateTime.now().startOf('day');
    const from = now.minus({ [type]: number }).toFormat('yyyyLLdd');
    const to = now.toFormat('yyyyLLdd');

    return `${from}-${to}`;
  };

  const getPresetDateRange = (option: string) => {
    switch (option) {
      case DATE_RANGE_LAST_DAY:
        return getDateRangeValues('days', 1);
      case DATE_RANGE_LAST_WEEK:
        return getDateRangeValues('weeks', 1);
      case DATE_RANGE_LAST_MONTH:
        return getDateRangeValues('months', 1);
      default:
        return '';
    }
  };

  const getSelectDateOptions = () => {
    const options = [
      { label: 'day', value: DATE_RANGE_LAST_DAY },
      { label: 'week', value: DATE_RANGE_LAST_WEEK },
      { label: 'month', value: DATE_RANGE_LAST_MONTH },
      { label: 'clear', value: DATE_RANGE_NO_LIMIT },
    ];

    return (
      <Box display='flex' alignItems='center' justifyContent='flex-end'>
        <Typography variant='body2' sx={{ mr: 0.5 }}>
          last:
        </Typography>
        {options.map((option, index) => (
          <Box key={option.label} display='flex' alignItems='center'>
            <Link
              component='button'
              variant='body2'
              onClick={() =>
                setData((prev) => ({
                  ...prev,
                  dateRange:
                    option.value === DATE_RANGE_NO_LIMIT
                      ? ''
                      : getPresetDateRange(option.value),
                }))
              }
              sx={{
                fontWeight: 600,
                textTransform: 'lowercase',
                mx: index < options.length - 1 ? 0.5 : 0,
              }}
            >
              {option.label}
            </Link>
            {index < options.length - 1 && (
              <Typography variant='body2' sx={{ mx: 0.5 }}>
                |
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const launchRun = async (e: any) => {
    e.preventDefault();
    setProgress(0);
    setProgressMax(100);
    setProgressMessage('Starting...');

    if (data.dateRange === INVALID_DATE_RANGE) {
      notificationStore.showMessage('Some fields are invalid.', 'error', {
        'Date Range': 'Date Range is not in a valid format.',
      });
      resetProgressBar();
      return;
    }

    const payload = {
      ...data,
      dateRange: data.dateRange || null,
    };

    const validError = validateForm(payload, validationSchema);
    if (!validError) {
      setErrors({});
      const resData: any = await DataStore.metricsLaunchRequest(payload);
      if (resData?.messageType === 'error') {
        resetProgressBar();
      } else if (resData) {
        showOutput(resData);
      }
    } else {
      resetProgressBar();
      const isRequiredMissing = Object.values(validError).some((msg) =>
        String(msg).toLowerCase().includes('is required')
      );
      const humanizedErrors = Object.entries(validError).reduce(
        (acc: any, [key, message]) => {
          const label = fieldLabels[key] || key;
          const safeMessage =
            typeof message === 'string' ? message : String(message);
          acc[label] = safeMessage.replace(new RegExp(key, 'gi'), label);
          return acc;
        },
        {}
      );
      notificationStore.showMessage(
        isRequiredMissing ? MISSING_FIELDS_MESSAGE : 'Some fields are invalid.',
        'error',
        humanizedErrors
      );
      setErrors(humanizedErrors);
    }
  };

  const getOptionsItems = () => {
    const pageConfiguration: any = getPageConfiguration('metrics');
    return (pageConfiguration?.metricsType || []).map(
      (item: string) => item.charAt(0).toUpperCase() + item.slice(1)
    );
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 12 }}>
          <BaseRadioButtons
            feature={'metrics'}
            items={getOptionsItems()}
            label='Options'
            value={data.metricsType}
            name={'metricsType'}
            onChange={onChangeData}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 2 }}>
          <BaseInput
            feature={'metrics'}
            label={fieldLabels.maxFiles}
            value={data.maxFiles ?? ''}
            name={'maxFiles'}
            onChange={onChangeData}
            errors={errors}
            type='number'
            inputProps={{
              min: 0,
            }}
            required={isMaxFilesRequired}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <BaseInput
            feature={'metrics'}
            label={fieldLabels.slideNumber}
            value={data.slideNumber}
            name={'slideNumber'}
            onChange={onChangeData}
            errors={errors}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <BaseInput
            feature={'metrics'}
            label={fieldLabels.wells}
            value={data.wells}
            name={'wells'}
            onChange={onChangeData}
            errors={errors}
            isDisabled={!data.slideNumber?.trim()}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <BaseInput
            feature={'metrics'}
            label={fieldLabels.keyword}
            value={data.keyword}
            name={'keyword'}
            onChange={onChangeData}
            errors={errors}
            tooltip='Search by part of the experiment folder name.'
          />
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <BaseDateRangePicker
            feature={'metrics'}
            value={data.dateRange ?? ''}
            name={'dateRange'}
            onChange={onChangeData}
            errors={errors}
            description={getSelectDateOptions()}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 10 }}>
          <BaseButton
            text={LAUNCH_BUTTON}
            variant='contained'
            color='primary'
            isDisabled={DataStore.isProcessing}
            spinner={DataStore.isProcessing}
            onClick={launchRun}
          />
        </Grid>
      </Grid>
      <ProgressBar
        actual={progress}
        max={progressMax}
        message={progressMessage}
      />
    </>
  );
});

export default Metrics;
